// @flow

import React from 'react';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import _ from 'lodash';
import i18n from 'i18n-js';
import { Table, Icon, Header } from 'semantic-grab-ui/src';

const invertedColStyle = { width: '30%', fontWeight: '400' };

const TableRow = ({ values, inverted }) => (
  <Table.Row>{
    _.map(values, (value, cellIndex) => (
      <Table.Cell
        key={`cell-${cellIndex}`}
        style={(!cellIndex && !!inverted) ? invertedColStyle : {}}
      >
        {value}
      </Table.Cell>
    ))
  }</Table.Row>
);

const TableBody = ({ rows, inverted }) => (
  <Table.Body>
    {rows.map((row, index) => (
      <TableRow key={`row-${index}`} index={index} values={row} inverted={inverted} />
    ))}
  </Table.Body>
);

const HandleBar = SortableHandle(({ value, index }) => (<Table.Cell className="move" key={`cell-${index}`}><Icon name="bars" />{value}</Table.Cell>));

const SortableTableRow = SortableElement(({ values }) => (
  <Table.Row>
    <HandleBar value={values[0]} index={0} />
    {_.map(_.slice(values, 1), (value, index) => <Table.Cell key={`cell-${index + 1}`}>{value}</Table.Cell>)}
  </Table.Row>
));

const SortableTableBody = SortableContainer(({ rows }) => (
  <Table.Body>
    {rows.map((row, index) => (
      <SortableTableRow key={`row-${index}`} index={index} values={row} />
    ))}
  </Table.Body>
));

const defaultTableProps = { striped: true };

type Props = {
  columns: Array<Object>,
  rows: Array<Object>,
  localeSelector?: string,
  sortable?: boolean,
  onSortEnd?: Function,
  tableProps?: Object,
  inverted?: boolean,
};

export const TableComponent = (props: Props) => {
  const { columns, rows, localeSelector, sortable, onSortEnd, tableProps = {}, inverted } = props;
  // convert all column items to valid column objects
  const columnObjs = _.map(_.filter(columns, (column) => !!column), (column) => {
    const obj = {};
    // Get key from column object, else column is not the object but the key string itself
    obj.key = _.get(column, 'key', column);
    // Get name from column.name, or from locales after prefixing localeSelector or at last resort just use the startCased key
    obj.name = _.get(column, 'name',
      (localeSelector && i18n.t(`${localeSelector}.${obj.key}`, { defaultValue: _.startCase(obj.key) }))
      || _.startCase(obj.key));
    // Get the renderFunc if present or set it as keyGetter from row
    obj.renderFunc = _.get(column, 'renderFunc', (row) => _.get(row, obj.key));
    return obj;
  });

  // Map out each rows and columns and calls its renderFunc
  let tableData = _.chain(rows)
    .map((row, rowIndex) =>
      _.map(columnObjs, (column) => column.renderFunc(row, rowIndex, props))
    )
    .filter(_.some)
    .value();

  if (inverted) {
    // Transpose the tableData matrix, include first column (row headers) from columnObjs
    tableData = _.map(tableData[0], (y, i) => [columnObjs[i].name, ..._.map(tableData, x => x[i])]);
  }
  const TableBodyWrapper = sortable ? SortableTableBody : TableBody;

  return (
    <Table {...{ ...defaultTableProps, ...tableProps }} definition={inverted}>
      {/* When inverted, column headers become row headers i.e. they are row's first element */}
      {!inverted && <Table.Header>
        <Table.Row>
          {_.map(columnObjs, (column, index) => <Table.HeaderCell key={`cell-${index}`}>{column.name}</Table.HeaderCell>)}
        </Table.Row>
      </Table.Header>}
      {tableData.length > 0
        ? <TableBodyWrapper
          inverted={inverted}
          rows={tableData}
          helperClass="sortableHelper"
          useDragHandle
          onSortEnd={onSortEnd}
        />
        : (
          <Table.Footer fullWidth>
            <Table.Row>
              <Table.HeaderCell colSpan={columnObjs.length}>
                <Header textAlign="center">{i18n.t('common.no_results')}</Header>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>)
      }
    </Table>
  );
};

export default TableComponent;
